<template>
  <div
    ref="header"
    :class="{
      'fixperience-header-fi': true,
      ...headerHeightClasses,
    }"
  >
    <div class="fixperience-header-fi__content">
      <div class="fixperience-header-fi__content-left">
        <a
          v-if="logoHeader?.src && homeData"
          :href="homeData.url"
          :title="homeData.title"
          class="fixperience-header-fi__logo"
        >
          <logo-header-fi
            :logo-header="logoHeader"
            :large="$fischer.largeLogo()"
          />
        </a>
      </div>
      <div class="fixperience-header-fi__content-center">
        <h1 class="fixperience-header-fi__headline">
          {{ $dict.get('fixperience.Header.Headline') || 'FiXperience Suite' }}
        </h1>
      </div>
      <div class="fixperience-header-fi__content-right">
        <div class="fi-hidden md:fi-flex">
          <div
            v-if="fixperienceStore.hasTutorials && (authenticated || isCn)"
            class="fixperience-header-fi__action"
            @click="triggerTutorials"
          >
            <icon-fi icon="book-open" />
            <span class="fixperience-header-fi__action-text">{{
              $dict.get('fixperience.Header.Tutorials')
            }}</span>
          </div>
          <div
            v-if="authenticated || isCn"
            class="fixperience-header-fi__action"
            @click="triggerLanguage"
          >
            <icon-fi icon="globe" />
            <span class="fixperience-header-fi__action-text">{{
              $dict.get('fixperience.Header.Language')
            }}</span>
          </div>
          <div
            v-if="authenticated"
            class="fixperience-header-fi__action--user"
          >
            <fixperience-header-menu-fi
              :logo-header="logoHeader"
              :home-data="homeData"
              @trigger-language="triggerLanguage"
              @trigger-tutorials="triggerTutorials"
            />
          </div>
        </div>
        <div class="fi-mr-18 fi-flex fi-items-center sm:fi-mr-30 md:fi-hidden">
          <fixperience-header-menu-fi
            :logo-header="logoHeader"
            :home-data="homeData"
            @trigger-language="triggerLanguage"
            @trigger-tutorials="triggerTutorials"
          />
        </div>
      </div>
      <toast-notification-fi v-if="!isEditMode" />
    </div>
    <dialog-modal-fi
      ref="tutorialsDialog"
      lazy
    >
      <template #body>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="richtext-fi"
          v-html="fields?.tutorials?.value"
        />
        <!-- eslint-enable vue/no-v-html -->
      </template>
    </dialog-modal-fi>
  </div>
</template>

<script setup lang="ts">
import { DialogModalFi, IconFi, LogoHeaderFi } from 'atoms';
import { useFixperienceStore } from 'store/fixperience';
import { computed, inject, useTemplateRef, watch } from 'vue';

import FixperienceHeaderMenuFi from '@/components/fixperience/fixperience-header-menu-fi/FixperienceHeaderMenuFi.vue';
import ToastNotificationFi from '@/components/notifications-fi/toast-notification-fi/ToastNotificationFi.vue';
import { useFischer } from '@/composables/fischerPlugin';
import { useHeader } from '@/composables/header';
import { useHeaderHeight } from '@/composables/headerHeight';
import type { ImageFieldValue, LinkFieldValue, RichTextField } from '@/sitecoreFieldTypes';

defineOptions({
  name: 'FixperienceHeaderFi',
});

const props = defineProps<{
  logoHeader?: ImageFieldValue;
  homeData?: LinkFieldValue;
}>();

const fields = inject<{
  tutorials?: RichTextField;
}>('fields');

const headerRef = useTemplateRef<HTMLDivElement>('header');
useHeader(headerRef);
const fixperienceStore = useFixperienceStore();
const fischer = useFischer();

const { headerHeightClasses } = useHeaderHeight();

const authenticated = computed<boolean>(() => fischer.user.authenticated());
const isCn = computed<boolean>(() => fischer.siteLanguage() === 'zh-CN');

const triggerLanguage = () => {
  if (fixperienceStore.hasLanguage) {
    fixperienceStore.openLanguages();
  } else {
    window.location.href = `${props.homeData?.url || '/'}?manual-language=true`;
  }
};

const tutorialsDialog = useTemplateRef<typeof DialogModalFi>('tutorialsDialog');
watch(
  () => fixperienceStore.tutorialClickCounter,
  (count) => {
    if (count > 0) {
      tutorialsDialog.value?.open();
    }
  },
);
const triggerTutorials = () => fixperienceStore.openTutorials();
fixperienceStore.hasTutorials = Boolean(fields?.tutorials?.value);
</script>

<style scoped src="./fixperience-header-fi" />

<style src="@/components/header-fi/header-fi-unscoped" />
